import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getInvoiceFileInfo, splitInvoice, mergeInvoices, formatApiError, money } from "@/lib/apiClient";
import { Loader2, Scissors, Merge } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const groupsFromBreaks = (n, breaks) => {
  const groups = [[1]];
  for (let p = 2; p <= n; p++) (breaks.has(p) ? groups.push([p]) : groups[groups.length - 1].push(p));
  return groups;
};

const pageState = (p, isBreak) => {
  if (p === 1) return <span className="text-muted-foreground">starts #1</span>;
  if (isBreak) return <span className="text-primary font-medium">new invoice</span>;
  return <span className="text-muted-foreground">continues</span>;
};

const PageTile = ({ p, src, isBreak, onToggle }) => (
  <button type="button" data-testid={`split-page-${p}`} disabled={p === 1} onClick={onToggle}
          className={cn("rounded-lg border p-1.5 text-left text-xs transition-colors", isBreak ? "border-primary bg-primary/10" : "border-border hover:border-muted-foreground/40", p === 1 && "opacity-70")}>
    {src ? <img src={src} alt={`Page ${p}`} className="w-full rounded bg-white aspect-[3/4] object-cover object-top" /> : <div className="aspect-[3/4] rounded bg-muted" />}
    <div className="mt-1 flex items-center justify-between"><span>Page {p}</span>{pageState(p, isBreak)}</div>
  </button>
);

export const SplitMergeDialog = ({ open, onOpenChange, inv, pages, user }) => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState("split");
  const [breaks, setBreaks] = useState(new Set());
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInfo(null); setBreaks(new Set()); setPicked(new Set());
    getInvoiceFileInfo(inv.id).then(setInfo).catch(() => setInfo({ has_file: false, merge_candidates: [] }));
  }, [open, inv.id]);

  const n = info?.page_count || inv.page_count || 0;
  const groups = groupsFromBreaks(n, breaks);
  const toggle = (set, v, setter) => { const s = new Set(set); s.has(v) ? s.delete(v) : s.add(v); setter(s); };

  const run = async () => {
    setBusy(true);
    try {
      if (mode === "split") {
        const r = await splitInvoice(inv.id, groups, user);
        const ok = r.created.filter((c) => c.id);
        toast.success(`Split into ${ok.length} invoice${ok.length === 1 ? "" : "s"}`);
        navigate(ok[0] ? `/invoice/${ok[0].id}` : "/review");
      } else {
        const r = await mergeInvoices([inv.id, ...picked], user);
        toast.success(`Merged into one invoice (${r.created.page_count} pages)`);
        navigate(`/invoice/${r.created.id}`);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not complete"));
    }
    setBusy(false);
  };

  const canSplit = info?.has_file && n > 1 && breaks.size > 0;
  const canMerge = info?.has_file && picked.size > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="split-merge-dialog">
        <DialogHeader>
          <DialogTitle>Split or merge this document</DialogTitle>
          <DialogDescription>Fix the automatic page detection: start new invoices on chosen pages, or combine documents that belong together.</DialogDescription>
        </DialogHeader>
        {!info && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
        {info && !info.has_file && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm" data-testid="split-merge-unavailable">The original PDF isn't stored for this invoice (it was created before page storage was added, or came from text/image), so it can't be split or merged.</div>
        )}
        {info?.has_file && (
          <>
            <div className="flex gap-1 rounded-lg border border-border bg-muted/30 p-1 w-fit">
              {[["split", "Split pages", Scissors], ["merge", "Merge with another", Merge]].map(([k, l, I]) => (
                <button key={k} data-testid={`split-merge-mode-${k}`} onClick={() => setMode(k)} className={cn("flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm", mode === k ? "bg-background shadow" : "text-muted-foreground")}><I className="h-3.5 w-3.5" /> {l}</button>
              ))}
            </div>
            {mode === "split" && n < 2 && <p className="text-sm text-muted-foreground" data-testid="split-single-page">This document has a single page — nothing to split. Use Merge if it belongs with another invoice.</p>}
            {mode === "split" && n >= 2 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Tick the pages where a <b>new invoice starts</b>. Result: {groups.map((g) => `p${g.join("-")}`).join(" · ")}</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[320px] overflow-auto d20-scroll pr-1">
                  {Array.from({ length: n }, (_, i) => i + 1).map((p) => (
                    <PageTile key={p} p={p} src={pages?.[p - 1]} isBreak={breaks.has(p)} onToggle={() => toggle(breaks, p, setBreaks)} />
                  ))}
                </div>
              </div>
            )}
            {mode === "merge" && info.merge_candidates.length === 0 && <p className="text-sm text-muted-foreground" data-testid="merge-none">No other open invoice from this supplier (or split group) has its PDF stored, so there's nothing to merge with.</p>}
            {mode === "merge" && info.merge_candidates.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Choose the invoice(s) to combine with this one. A single new invoice is created from all pages and re-read.</p>
                <ul className="divide-y divide-border rounded-lg border border-border max-h-[280px] overflow-auto d20-scroll">
                  {info.merge_candidates.map((c) => (
                    <li key={c.id}><label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-muted/30">
                      <input type="checkbox" data-testid={`merge-pick-${c.id}`} checked={picked.has(c.id)} onChange={() => toggle(picked, c.id, setPicked)} />
                      <span className="flex-1 truncate">{c.supplier} <span className="font-mono text-muted-foreground">{c.invoice_number || "(no number)"}</span></span>
                      <span className="font-mono text-xs">{money(c.total, c.currency)} · {c.page_count} p</span>
                    </label></li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="split-merge-run" onClick={run} disabled={busy || !(mode === "split" ? canSplit : canMerge)}>
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            {!busy && (mode === "split" ? <Scissors className="h-4 w-4 mr-1.5" /> : <Merge className="h-4 w-4 mr-1.5" />)}
            {mode === "split" ? `Split into ${groups.length}` : `Merge ${picked.size + 1} invoices`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
